import { Static, Type } from '@sinclair/typebox';
import { MessageRole, ToolInvocationStatus } from './assistant';

/**
 * Pulse admin — un agent Propelsia interroge Pulse sur un pro, en lecture seule, depuis la
 * fiche pro de l'admin (cf. docs/to_implement/pulse-admin-plan.md). Fils sauvegardés dans des
 * tables dédiées (`admin_pulse_*`), jamais visibles du pro.
 */

/** Longueur max d'une question d'agent (même borne que le message support envoyé à Pulse). */
export const ADMIN_PULSE_MESSAGE_MAX_LENGTH = 4000;
/** Longueur du libellé de fil dérivé du 1er message. */
export const ADMIN_PULSE_TITLE_MAX_LENGTH = 160;

export const AdminPulseMessageBodySchema = Type.Object({
  /** Absent = ouvre un nouveau fil sur ce pro. */
  conversation_id: Type.Optional(Type.Integer({ minimum: 1 })),
  message: Type.String({ minLength: 1, maxLength: ADMIN_PULSE_MESSAGE_MAX_LENGTH }),
  /** Conversation Chatwoot d'origine quand la question part de la mini-app Chatwoot. */
  chatwoot_conversation_id: Type.Optional(Type.Integer({ minimum: 1 })),
});

/** Une lecture faite par Pulse pendant le tour : l'agent voit sur quoi repose la réponse. */
export const AdminPulseToolReadSchema = Type.Object({
  tool_name: Type.String(),
  status: Type.Enum(ToolInvocationStatus),
  args: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  /** Extrait du résultat renvoyé par l'outil. */
  result_excerpt: Type.Optional(Type.String()),
  error: Type.Optional(Type.String()),
  duration_ms: Type.Optional(Type.Number()),
});

export const AdminPulseMessageSchema = Type.Object({
  message_id: Type.Number(),
  role: Type.Enum(MessageRole),
  content: Type.String(),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
  tool_reads: Type.Array(AdminPulseToolReadSchema),
});

export const AdminPulseConversationSummarySchema = Type.Object({
  conversation_id: Type.Number(),
  title: Type.Optional(Type.String()),
  admin_user_id: Type.Number(),
  admin_user_name: Type.String(),
  /** Seul l'auteur d'un fil peut le poursuivre ; les autres agents le lisent. */
  is_mine: Type.Boolean(),
  last_message_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_message_at', with_time: true } }),
  ),
});

export const AdminPulseConversationListResponseSchema = Type.Object({
  /** Kill switch `ADMIN_PULSE_ENABLED` : à `false`, le front affiche l'historique sans zone de saisie. */
  enabled: Type.Boolean(),
  items: Type.Array(AdminPulseConversationSummarySchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

export const AdminPulseConversationResponseSchema = Type.Object({
  conversation: AdminPulseConversationSummarySchema,
  professional_id: Type.Number(),
  messages: Type.Array(AdminPulseMessageSchema),
});

export const AdminPulseAnswerResponseSchema = Type.Object({
  conversation: AdminPulseConversationSummarySchema,
  /** Le message de l'agent tel que persisté, puis la réponse de Pulse. */
  messages: Type.Array(AdminPulseMessageSchema),
});

export const AdminPulseDeleteResponseSchema = Type.Object({
  conversation_id: Type.Number(),
});

export type AdminPulseMessageBody = Static<typeof AdminPulseMessageBodySchema>;
export type AdminPulseToolRead = Static<typeof AdminPulseToolReadSchema>;
export type AdminPulseMessage = Static<typeof AdminPulseMessageSchema>;
export type AdminPulseConversationSummary = Static<typeof AdminPulseConversationSummarySchema>;
export type AdminPulseConversationListResponse = Static<typeof AdminPulseConversationListResponseSchema>;
export type AdminPulseConversationResponse = Static<typeof AdminPulseConversationResponseSchema>;
export type AdminPulseAnswerResponse = Static<typeof AdminPulseAnswerResponseSchema>;
export type AdminPulseDeleteResponse = Static<typeof AdminPulseDeleteResponseSchema>;
